import { assertReady, checkPickleKey, consume, Handle, raw } from "./wasm.js";

export interface IdentityKeys {
  curve25519: string;
  ed25519: string;
}

/** An Olm message. Type 0 is a pre-key message, type 1 a normal message. */
export interface OlmMessage {
  type: 0 | 1;
  body: string;
}

function messageType(value: number): 0 | 1 {
  if (value === 0 || value === 1) return value;
  throw new Error(`unexpected Olm message type ${value}`);
}

function preKeyBody(message: string | OlmMessage): string {
  if (typeof message === "string") return message;
  if (message.type !== 0) throw new Error("expected a pre-key message (type 0)");
  return message.body;
}

/** The long term identity of one device: identity keys, one-time keys and the fallback key. */
export class Account extends Handle<raw.Account> {
  private constructor(inner: raw.Account) {
    super(inner);
  }

  static create(): Account {
    assertReady();
    return new Account(new raw.Account());
  }

  static unpickle(pickle: string, key: Uint8Array): Account {
    assertReady();
    checkPickleKey(key);
    return new Account(raw.Account.unpickle(pickle, key));
  }

  identityKeys(): IdentityKeys {
    return { curve25519: this.inner.curve25519Key(), ed25519: this.inner.ed25519Key() };
  }

  /** Signs with the Ed25519 identity key. Returns a base64 signature. */
  sign(message: string): string {
    return this.inner.sign(message);
  }

  /** How many one-time keys the server should hold for this device. */
  maxOneTimeKeys(): number {
    return this.inner.maxOneTimeKeys();
  }

  generateOneTimeKeys(count: number): void {
    this.inner.generateOneTimeKeys(count);
  }

  /** Unpublished one-time keys, keyed by key id. */
  oneTimeKeys(): Record<string, string> {
    return JSON.parse(this.inner.oneTimeKeysJson()) as Record<string, string>;
  }

  generateFallbackKey(): void {
    this.inner.generateFallbackKey();
  }

  /** The unpublished fallback key, keyed by key id. Empty once published. */
  fallbackKey(): Record<string, string> {
    return JSON.parse(this.inner.fallbackKeyJson()) as Record<string, string>;
  }

  /** Drops the previous fallback key. Returns false when there was none. */
  forgetFallbackKey(): boolean {
    return this.inner.forgetFallbackKey();
  }

  markKeysAsPublished(): void {
    this.inner.markKeysAsPublished();
  }

  createOutboundSession(theirIdentityKey: string, theirOneTimeKey: string): OlmSession {
    return OlmSession.wrap(this.inner.createOutboundSession(theirIdentityKey, theirOneTimeKey));
  }

  /** Creates a session from a received pre-key message and removes the one-time key it used. */
  createInboundSession(
    theirIdentityKey: string,
    preKeyMessage: string | OlmMessage,
  ): { session: OlmSession; plaintext: string } {
    return consume(
      this.inner.createInboundSession(theirIdentityKey, preKeyBody(preKeyMessage)),
      (result) => ({
        session: OlmSession.wrap(result.takeSession()),
        plaintext: result.plaintext,
      }),
    );
  }

  /** Encrypts the account with a 32 byte key. Storing that key safely is the caller's job. */
  pickle(key: Uint8Array): string {
    checkPickleKey(key);
    return this.inner.pickle(key);
  }
}

/** A Double Ratchet session between two devices. */
export class OlmSession extends Handle<raw.Session> {
  private constructor(inner: raw.Session) {
    super(inner);
  }

  /** @internal */
  static wrap(inner: raw.Session): OlmSession {
    return new OlmSession(inner);
  }

  static unpickle(pickle: string, key: Uint8Array): OlmSession {
    assertReady();
    checkPickleKey(key);
    return new OlmSession(raw.Session.unpickle(pickle, key));
  }

  sessionId(): string {
    return this.inner.sessionId();
  }

  /** True once a message from the other side has been decrypted. Until then messages are pre-key messages. */
  hasReceivedMessage(): boolean {
    return this.inner.hasReceivedMessage();
  }

  encrypt(plaintext: string): OlmMessage {
    return consume(this.inner.encrypt(plaintext), (message) => ({
      type: messageType(message.messageType),
      body: message.body,
    }));
  }

  decrypt(message: OlmMessage): string {
    return this.inner.decrypt(message.type, message.body);
  }

  /** True when a pre-key message from `theirIdentityKey` belongs to this session. */
  matchesPreKey(theirIdentityKey: string, message: string | OlmMessage): boolean {
    return this.inner.matchesPreKey(theirIdentityKey, preKeyBody(message));
  }

  pickle(key: Uint8Array): string {
    checkPickleKey(key);
    return this.inner.pickle(key);
  }
}

/** Verifies an Ed25519 signature. Malformed input returns false. */
export function verifySignature(ed25519Key: string, message: string, signature: string): boolean {
  assertReady();
  return raw.verifySignature(ed25519Key, message, signature);
}
